<script setup lang="ts">
import { type PowertrainWarrantyDurationDto, useApiGetPowertrainWarrantyDurations } from '~/apiClient';

// TYPES
export type VasPowertrainWarrantyDuration = Pick<PowertrainWarrantyDurationDto, 'durationMonths' | 'mileageLimitKm'>;

// MODELS
const duration = defineModel<VasPowertrainWarrantyDuration | undefined>('duration', { required: true });
const price = defineModel<number | undefined>('price');

// COMPOSABLES
const { tt, formatUnits } = useTypedI18n();

// DATA
const { data, isPending: isPendingDuration } = useApiGetPowertrainWarrantyDurations();
const durationItems = computed(() =>
    data.value?.durations.map(d => ({
        ...d,
        label: tt('vas.PowertrainWarranty.duration.itemLabel', { months: d.durationMonths, km: formatUnits.km(d.mileageLimitKm) }),
        value: d.durationMonths,
    }))
);
const durationModel = ref(duration.value?.durationMonths);

// WATCHERS
watch(
    () => duration.value?.durationMonths,
    (value, oldValue) => {
        if (value !== oldValue) {
            durationModel.value = value;
        }
    }
);

watch(durationModel, value => {
    const item = durationItems.value?.find(di => di.value === value);
    if (!item) return;

    duration.value = {
        durationMonths: item.durationMonths,
        mileageLimitKm: item.mileageLimitKm,
    };
    price.value = item.price;
});
</script>

<template>
    <UFormField name="powertrainWarrantyDuration" :label="tt('vas.PowertrainWarranty.duration.label')">
        <USelectMenu
            v-model="durationModel"
            :placeholder="tt('vas.PowertrainWarranty.duration.placeholder')"
            :loading="isPendingDuration"
            :items="durationItems"
            :search-input="false"
            value-key="value"
        />
    </UFormField>
</template>
